import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SUPPLIERS_KEYS,
  adicionarDocumentoFornecedor,
  atualizarFornecedor,
  criarFornecedor,
  listarFornecedores,
  listarNotificacoesPortalFornecedor,
  listarTiposDocumentoFornecedor,
  atualizarPortalFornecedor,
  obterDashboardFornecedores,
  obterFornecedor,
  obterPortalFornecedor,
  registrarAprovacaoFornecedor,
  registrarAvaliacaoFornecedor,
  revisarDocumentoFornecedor,
  type CreateSupplierApprovalPayload,
  type CreateSupplierDocumentPayload,
  type CreateSupplierEvaluationPayload,
  type CreateSupplierPayload,
  type Supplier,
  type SupplierPortalUpdatePayload,
  type SupplierStatus,
  type UpdateSupplierPayload,
} from '../api';

interface ListParams {
  status?: SupplierStatus | '';
  category?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export function useFornecedores(params?: ListParams) {
  return useQuery({
    queryKey: SUPPLIERS_KEYS.list(params),
    queryFn: () => listarFornecedores(params),
  });
}

export function useDashboardFornecedores() {
  return useQuery({
    queryKey: SUPPLIERS_KEYS.dashboard,
    queryFn: obterDashboardFornecedores,
  });
}

export function useFornecedor(id: string | null) {
  return useQuery({
    queryKey: SUPPLIERS_KEYS.detail(id ?? ''),
    queryFn: () => obterFornecedor(id as string),
    enabled: Boolean(id),
  });
}

export function useTiposDocumentoFornecedor() {
  return useQuery({
    queryKey: SUPPLIERS_KEYS.documentTypes,
    queryFn: listarTiposDocumentoFornecedor,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePortalFornecedor() {
  return useQuery({
    queryKey: SUPPLIERS_KEYS.portal,
    queryFn: obterPortalFornecedor,
  });
}

export function useNotificacoesPortalFornecedor(supplierId: string | null) {
  return useQuery({
    queryKey: SUPPLIERS_KEYS.portalNotifications(supplierId ?? ''),
    queryFn: () => listarNotificacoesPortalFornecedor(supplierId as string),
    enabled: Boolean(supplierId),
    refetchInterval: 30_000,
  });
}

export function useAtualizarPortalFornecedor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SupplierPortalUpdatePayload) => atualizarPortalFornecedor(payload),
    onSuccess: (supplier) => {
      queryClient.setQueryData(SUPPLIERS_KEYS.portal, supplier);
      queryClient.setQueryData(SUPPLIERS_KEYS.detail(supplier.id), supplier);
      return queryClient.invalidateQueries({ queryKey: SUPPLIERS_KEYS.all });
    },
  });
}

export function useCriarFornecedor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSupplierPayload) => criarFornecedor(payload),
    onSuccess: (supplier) => refreshSupplierQueries(queryClient, supplier),
  });
}

export function useAtualizarFornecedor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSupplierPayload }) => atualizarFornecedor(id, payload),
    onSuccess: (supplier) => refreshSupplierQueries(queryClient, supplier),
  });
}

export function useAdicionarDocumentoFornecedor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload, file }: { id: string; payload: CreateSupplierDocumentPayload; file: File }) =>
      adicionarDocumentoFornecedor(id, payload, file),
    onSuccess: (supplier) => refreshSupplierQueries(queryClient, supplier),
  });
}

export function useRevisarDocumentoFornecedor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId, documentId, payload }: { supplierId: string; documentId: string; payload: { status: 'aprovado' | 'reprovado'; rejectionReason?: string } }) =>
      revisarDocumentoFornecedor(supplierId, documentId, payload),
    onSuccess: (supplier) => refreshSupplierQueries(queryClient, supplier),
  });
}

export function useRegistrarAprovacaoFornecedor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateSupplierApprovalPayload }) => registrarAprovacaoFornecedor(id, payload),
    onSuccess: (supplier) => refreshSupplierQueries(queryClient, supplier),
  });
}

export function useRegistrarAvaliacaoFornecedor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateSupplierEvaluationPayload }) => registrarAvaliacaoFornecedor(id, payload),
    onSuccess: (supplier) => refreshSupplierQueries(queryClient, supplier),
  });
}

function refreshSupplierQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  supplier: Supplier,
) {
  queryClient.setQueryData(SUPPLIERS_KEYS.detail(supplier.id), supplier);
  return queryClient.invalidateQueries({ queryKey: SUPPLIERS_KEYS.all });
}
